import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Loader2,
  Receipt,
  Plus,
  Check,
  X,
  Camera,
  ImageIcon,
  Filter,
} from 'lucide-react';
import { toast } from 'sonner';
import AppLayout from '@/layouts/AppLayout';

const CATEGORIES = {
  cleaning_supplies: { ru: 'Химия / расходники', en: 'Cleaning supplies' },
  equipment: { ru: 'Оборудование', en: 'Equipment' },
  fuel: { ru: 'Бензин', en: 'Fuel' },
  travel: { ru: 'Поездка', en: 'Travel' },
  office: { ru: 'Офис', en: 'Office' },
  advertising: { ru: 'Реклама', en: 'Advertising' },
  insurance: { ru: 'Страховка', en: 'Insurance' },
  rent: { ru: 'Аренда', en: 'Rent' },
  utilities: { ru: 'Коммунальные', en: 'Utilities' },
  other: { ru: 'Другое', en: 'Other' },
};

const STATUS_CONFIG = {
  pending: { label: { ru: 'Ожидает', en: 'Pending' }, className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  approved: { label: { ru: 'Одобрен', en: 'Approved' }, className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  rejected: { label: { ru: 'Отклонён', en: 'Rejected' }, className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
  synced: { label: { ru: 'В QB', en: 'Synced' }, className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
};

function formatDate(iso) {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function ExpensesPage() {
  const { api, user } = useAuth();
  const { lang } = useLanguage();
  const [expenses, setExpenses] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);

  // Filters
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');

  const fetchExpenses = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterType !== 'all') params.expense_type = filterType;
      const res = await api.get('/expenses/', { params });
      setExpenses(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  }, [api, filterStatus, filterType]);

  useEffect(() => { fetchExpenses(); }, [fetchExpenses]);

  const handleApprove = async (id) => {
    try {
      await api.post(`/expenses/${id}/approve`);
      toast.success(lang === 'ru' ? 'Расход одобрен' : 'Expense approved');
      fetchExpenses();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const handleReject = async (id) => {
    try {
      await api.post(`/expenses/${id}/reject`);
      toast.success(lang === 'ru' ? 'Расход отклонён' : 'Expense rejected');
      fetchExpenses();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const totalApproved = expenses
    .filter(e => e.status === 'approved' || e.status === 'synced')
    .reduce((s, e) => s + (e.amount || 0), 0);

  return (
    <AppLayout>
      <div className="p-4 md:p-6 space-y-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Receipt className="h-6 w-6" />
              {lang === 'ru' ? 'Расходы компании' : 'Company Expenses'}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {lang === 'ru' ? 'Общие расходы и расходы по заказам' : 'General and order-linked expenses'}
            </p>
          </div>
          <Button onClick={() => setShowAddDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            {lang === 'ru' ? 'Добавить расход' : 'Add Expense'}
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="text-sm text-muted-foreground">{lang === 'ru' ? 'Всего записей' : 'Total records'}</div>
              <div className="text-2xl font-bold">{total}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="text-sm text-muted-foreground">{lang === 'ru' ? 'Ожидают одобрения' : 'Pending approval'}</div>
              <div className="text-2xl font-bold text-amber-500">
                {expenses.filter(e => e.status === 'pending').length}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="text-sm text-muted-foreground">{lang === 'ru' ? 'Одобрено (сумма)' : 'Approved total'}</div>
              <div className="text-2xl font-bold text-green-600">
                ${totalApproved.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{lang === 'ru' ? 'Все статусы' : 'All statuses'}</SelectItem>
              <SelectItem value="pending">{lang === 'ru' ? 'Ожидает' : 'Pending'}</SelectItem>
              <SelectItem value="approved">{lang === 'ru' ? 'Одобрен' : 'Approved'}</SelectItem>
              <SelectItem value="rejected">{lang === 'ru' ? 'Отклонён' : 'Rejected'}</SelectItem>
              <SelectItem value="synced">{lang === 'ru' ? 'В QB' : 'Synced'}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{lang === 'ru' ? 'Все типы' : 'All types'}</SelectItem>
              <SelectItem value="order">{lang === 'ru' ? 'По заказу' : 'Order'}</SelectItem>
              <SelectItem value="general">{lang === 'ru' ? 'Общие' : 'General'}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : expenses.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                {lang === 'ru' ? 'Нет расходов' : 'No expenses found'}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{lang === 'ru' ? 'Дата' : 'Date'}</TableHead>
                    <TableHead>{lang === 'ru' ? 'Тип' : 'Type'}</TableHead>
                    <TableHead>{lang === 'ru' ? 'Категория' : 'Category'}</TableHead>
                    <TableHead>{lang === 'ru' ? 'Описание' : 'Description'}</TableHead>
                    <TableHead className="text-right">{lang === 'ru' ? 'Сумма' : 'Amount'}</TableHead>
                    <TableHead>{lang === 'ru' ? 'Статус' : 'Status'}</TableHead>
                    <TableHead>{lang === 'ru' ? 'Чек' : 'Receipt'}</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {expenses.map((exp) => {
                    const cat = CATEGORIES[exp.category] || CATEGORIES.other;
                    const st = STATUS_CONFIG[exp.status] || STATUS_CONFIG.pending;
                    return (
                      <TableRow key={exp.id}>
                        <TableCell className="text-xs">{formatDate(exp.created_at)}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {exp.expense_type === 'order'
                              ? (lang === 'ru' ? 'Заказ' : 'Order')
                              : (lang === 'ru' ? 'Общий' : 'General')
                            }
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">{cat[lang]}</TableCell>
                        <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                          {exp.description || '-'}
                        </TableCell>
                        <TableCell className="text-right font-medium tabular-nums">
                          ${exp.amount.toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <Badge className={st.className}>{st.label[lang]}</Badge>
                        </TableCell>
                        <TableCell>
                          {exp.receipt_file_id && (
                            <ImageIcon className="h-4 w-4 text-muted-foreground" />
                          )}
                        </TableCell>
                        <TableCell>
                          {exp.status === 'pending' && (
                            <div className="flex gap-1">
                              <Button size="icon" variant="ghost" className="h-7 w-7 text-green-600" onClick={() => handleApprove(exp.id)}>
                                <Check className="h-3.5 w-3.5" />
                              </Button>
                              <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => handleReject(exp.id)}>
                                <X className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Add General Expense Dialog */}
        <AddGeneralExpenseDialog
          open={showAddDialog}
          onOpenChange={setShowAddDialog}
          api={api}
          lang={lang}
          onSuccess={fetchExpenses}
        />
      </div>
    </AppLayout>
  );
}

function AddGeneralExpenseDialog({ open, onOpenChange, api, lang, onSuccess }) {
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState('other');
  const [description, setDescription] = useState('');
  const [receiptFile, setReceiptFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error(lang === 'ru' ? 'Укажите сумму' : 'Enter amount');
      return;
    }

    setSaving(true);
    try {
      const res = await api.post('/expenses/', {
        expense_type: 'general',
        amount: parseFloat(amount),
        category,
        description: description || undefined,
      });

      if (receiptFile && res.data.id) {
        const formData = new FormData();
        formData.append('file', receiptFile);
        await api.post(`/expenses/${res.data.id}/receipt`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      toast.success(lang === 'ru' ? 'Расход добавлен' : 'Expense added');
      onOpenChange(false);
      setAmount('');
      setCategory('other');
      setDescription('');
      setReceiptFile(null);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>
            {lang === 'ru' ? 'Добавить общий расход' : 'Add General Expense'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-1 block">
              {lang === 'ru' ? 'Сумма ($)' : 'Amount ($)'}
            </label>
            <Input
              type="number"
              step="0.01"
              min="0.01"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              autoFocus
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-1 block">
              {lang === 'ru' ? 'Категория' : 'Category'}
            </label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(CATEGORIES).map(([key, label]) => (
                  <SelectItem key={key} value={key}>{label[lang]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium mb-1 block">
              {lang === 'ru' ? 'Описание' : 'Description'}
            </label>
            <Input
              placeholder={lang === 'ru' ? 'Что было куплено...' : 'What was purchased...'}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-1 block">
              {lang === 'ru' ? 'Фото чека' : 'Receipt photo'}
            </label>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={() => document.getElementById('general-receipt-input').click()}
              >
                <Camera className="h-4 w-4 mr-2" />
                {receiptFile
                  ? receiptFile.name
                  : (lang === 'ru' ? 'Прикрепить чек' : 'Attach receipt')
                }
              </Button>
              {receiptFile && (
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setReceiptFile(null)}>
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            <input
              id="general-receipt-input"
              type="file"
              accept="image/*,application/pdf"
              capture="environment"
              className="hidden"
              onChange={(e) => setReceiptFile(e.target.files?.[0] || null)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            {lang === 'ru' ? 'Отмена' : 'Cancel'}
          </Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {lang === 'ru' ? 'Добавить' : 'Add'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
